import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { X, ZoomIn } from 'lucide-react';

/* ─── Structured Data (JSON-LD) ──────────────────────────────────────────── */
const GALLERY_STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://www.yashodatotalsolution.in/gallery/#webpage',
      url: 'https://www.yashodatotalsolution.in/gallery/',
      name: 'Gallery | Yashoda Total Solution – Our Team, Office & Work',
      isPartOf: { '@id': 'https://www.yashodatotalsolution.in/#website' },
      description:
        'Explore the Yashoda Total Solution gallery — photos of our team, modern office space, client consultation sessions, document review work, and successful claim resolutions across India.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://www.yashodatotalsolution.in/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Gallery',
            item: 'https://www.yashodatotalsolution.in/gallery/',
          },
        ],
      },
    },
  ],
};

const GALLERY_IMAGES = [
  {
    src: 'https://static.prod-images.emergentagent.com/jobs/bd5b376d-ca14-453c-bb4f-06833e2e2741/images/e5979f4272bf2f229ef6c4109d92b735fd6320a814a55df4e166c6aa8121b0ef.png',
    category: 'office',
    caption: { en: 'Our Modern Office Space', hi: 'हमारा आधुनिक कार्यालय', mr: 'आमचे आधुनिक कार्यालय' },
  },
  {
    src: 'https://static.prod-images.emergentagent.com/jobs/bd5b376d-ca14-453c-bb4f-06833e2e2741/images/a77e675bc9c889cced8e0dc877a294d8b38c35aa8b3c724861068f18b31c6cb8.png',
    category: 'work',
    caption: { en: 'Client Consultation Session', hi: 'ग्राहक परामर्श सत्र', mr: 'ग्राहक सल्लामसलत सत्र' },
  },
  {
    src: 'https://static.prod-images.emergentagent.com/jobs/bd5b376d-ca14-453c-bb4f-06833e2e2741/images/054e21245c6ad145bb691d92c7d2c4b3cb0368a62ef6eaabd159bfafd715c95f.png',
    category: 'work',
    caption: { en: 'Document Review Process', hi: 'दस्तावेज़ समीक्षा प्रक्रिया', mr: 'दस्तऐवज पुनरावलोकन प्रक्रिया' },
  },
  {
    src: 'https://static.prod-images.emergentagent.com/jobs/bd5b376d-ca14-453c-bb4f-06833e2e2741/images/4da14e890f4bf43017dfe4592c31cc1c20ae811199067b7b31d6cdca8ac4f73a.png',
    category: 'success',
    caption: { en: 'Successful Claim Resolution', hi: 'सफल दावा समाधान', mr: 'यशस्वी दावा निराकरण' },
  },
  {
    src: 'https://static.prod-images.emergentagent.com/jobs/bd5b376d-ca14-453c-bb4f-06833e2e2741/images/ffa1856565e9893af507bedd3a7028f927ba4972b10196390cc2e4a098d78712.png',
    category: 'team',
    caption: { en: 'Our Managing Director', hi: 'हमारे प्रबंध निदेशक', mr: 'आमचे व्यवस्थापकीय संचालक' },
  },
  {
    src: 'https://static.prod-images.emergentagent.com/jobs/bd5b376d-ca14-453c-bb4f-06833e2e2741/images/87447e00c2f040c5a6f2bfadb1ae27a431d8b28a7e9a40f86d3b3d5a785b21c4.png',
    category: 'team',
    caption: { en: 'Our Dedicated Sales Team', hi: 'हमारी समर्पित बिक्री टीम', mr: 'आमची समर्पित विक्री टीम' },
  },
  {
    src: 'https://static.prod-images.emergentagent.com/jobs/bd5b376d-ca14-453c-bb4f-06833e2e2741/images/a75fe35cf9732eed2e87f9cab7303f4159c6a291444cd1c3688266fe8ac59960.png',
    category: 'team',
    caption: { en: 'Our Expert Legal Team', hi: 'हमारी विशेषज्ञ कानूनी टीम', mr: 'आमची तज्ञ कायदेशीर टीम' },
  },
];

const CATEGORIES = ['team', 'office', 'work', 'success'];

const galleryText = {
  en: {
    title: 'Gallery',
    subtitle: 'A glimpse into our workspace, team, and the work we do for our clients',
    team: 'Team',
    office: 'Office',
    work: 'Our Work',
    success: 'Success Stories',
  },
  hi: {
    title: 'गैलरी',
    subtitle: 'हमारे कार्यस्थल, टीम और ग्राहकों के लिए हमारे काम की एक झलक',
    team: 'टीम',
    office: 'कार्यालय',
    work: 'हमारा काम',
    success: 'सफलता की कहानियां',
  },
  mr: {
    title: 'गॅलरी',
    subtitle: 'आमचे कार्यस्थळ, टीम आणि ग्राहकांसाठी आम्ही करत असलेल्या कामाची एक झलक',
    team: 'टीम',
    office: 'कार्यालय',
    work: 'आमचे काम',
    success: 'यशोगाथा',
  },
};

const Gallery = () => {
  const { language } = useLanguage();
  const txt = galleryText[language];
  const [activeCategory, setActiveCategory] = useState('team');
  const [lightboxImage, setLightboxImage] = useState(null);

  const filteredImages = GALLERY_IMAGES.filter(img => img.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#F5F7F9]">

      {/* ── React Helmet SEO ──────────────────────────────────────────────── */}
      <Helmet prioritizeSeoTags>
        {/* Primary */}
        <title>Gallery | Yashoda Total Solution – Our Team, Office & Work</title>
        <meta
          name="description"
          content="Explore the Yashoda Total Solution gallery. See photos of our team, modern office, client consultation sessions, document review work and successful insurance claim resolutions across India."
        />
        <meta
          name="keywords"
          content="Yashoda Total Solution gallery, insurance claim team India, legal support office Mumbai, Yashoda team photos, insurance consultation office India"
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="Yashoda Total Solution" />

        {/* Canonical */}
        <link rel="canonical" href="https://www.yashodatotalsolution.in/gallery/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.yashodatotalsolution.in/gallery/" />
        <meta property="og:title" content="Gallery | Yashoda Total Solution – Our Team, Office & Work" />
        <meta
          property="og:description"
          content="Explore the Yashoda Total Solution gallery — photos of our team, office, client consultations and successful insurance claim resolutions across India."
        />
        <meta property="og:image" content="https://www.yashodatotalsolution.in/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Yashoda Total Solution Gallery" />
        <meta property="og:site_name" content="Yashoda Total Solution" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter / X Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gallery | Yashoda Total Solution – Our Team, Office & Work" />
        <meta
          name="twitter:description"
          content="Explore the Yashoda Total Solution gallery — photos of our team, office, client consultations and successful insurance claim resolutions across India."
        />
        <meta name="twitter:image" content="https://www.yashodatotalsolution.in/og-image.jpg" />
        <meta name="twitter:image:alt" content="Yashoda Total Solution Gallery" />

        {/* Geo & Region */}
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content="Mumbai, Maharashtra, India" />
        <meta name="geo.position" content="19.0722;72.8797" />
        <meta name="ICBM" content="19.0722, 72.8797" />

        {/* Structured Data JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(GALLERY_STRUCTURED_DATA)}
        </script>
      </Helmet>

      {/* Hero */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-[#0F7A4A] via-[#0A5734] to-[#0F7A4A] text-white py-20 md:py-32"
        aria-labelledby="gallery-hero-heading"
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1
              id="gallery-hero-heading"
              className="text-4xl md:text-6xl font-bold mb-4"
              data-testid="gallery-hero-title"
            >
              {txt.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              {txt.subtitle}
            </p>
          </motion.div>
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 h-16 bg-[#F5F7F9]"
          style={{ clipPath: 'ellipse(75% 100% at 50% 100%)' }}
          aria-hidden="true"
        />
      </section>

      {/* Filter Tabs */}
      <section className="pt-10 pb-2" aria-label="Gallery category filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3" role="group" aria-label="Filter gallery by category">
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                aria-pressed={activeCategory === category}
                aria-label={`Show ${txt[category]} images`}
                className={`relative px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-[#0F7A4A] text-white shadow-lg shadow-[#0F7A4A]/30'
                    : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm border border-gray-200'
                }`}
                data-testid={`filter-tab-${category}`}
              >
                {activeCategory === category && (
                  <motion.span
                    layoutId="activeTab"
                    className="absolute inset-0 bg-[#0F7A4A] rounded-full -z-10"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
                    aria-hidden="true"
                  />
                )}
                {txt[category]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section
        className="py-10 md:py-16"
        aria-label={`Gallery images — ${txt[activeCategory]}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredImages.map((img, index) => (
                <motion.div
                  key={img.src}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer bg-white hover:shadow-xl transition-shadow duration-300"
                  onClick={() => setLightboxImage(img)}
                  data-testid={`gallery-image-${index}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`View full size: ${img.caption[language]}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setLightboxImage(img);
                    }
                  }}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={img.src}
                      alt={img.caption[language]}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>
                  <div
                    className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <ZoomIn className="h-12 w-12 text-white" />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setLightboxImage(null)}
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`Lightbox: ${lightboxImage.caption[language]}`}
          >
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute top-6 right-6 text-white/80 hover:text-white z-50 focus:outline-none focus:ring-2 focus:ring-white rounded"
              data-testid="gallery-lightbox-close"
              aria-label="Close lightbox"
            >
              <X className="h-8 w-8" aria-hidden="true" />
            </button>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="max-w-5xl w-full"
            >
              <img
                src={lightboxImage.src}
                alt={lightboxImage.caption[language]}
                className="w-full max-h-[80vh] object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Gallery;